/** Matches v4 store checkout design-group flow (`STORE_DESIGN_TOOL.FROM_DESIGN_GROUP`). */
import { finalizeHqOutboundUrl } from '@/components/utils/FunctionUtils';

export const DESIGN_TOOL_FROM_DESIGN_GROUP_CHECKOUT = 'orders-checkout';

/** Default HQ source when opening design tool to add color/product options. */
export const DESIGN_TOOL_FROM_DESIGN_GROUP_HQ = 'HQ';

/** Design page source when opening design tool to add color/product options. */
export const DESIGN_TOOL_FROM_DESIGN_GROUP_HQ_DESIGN = 'HQDesign';

/** Store cart source when opening design tool to add color/product options. */
export const DESIGN_TOOL_FROM_DESIGN_GROUP_HQ_CART = 'HQCart';

interface IBuildDesignGroupDesignToolUrlParams {
  baseUrl: string;
  proofItemId: number;
  fromDesignGroup?: string;
  designId?: number;
}

/**
 * Builds design tool URL for add color/product option flows.
 * Defaults to `fromDesignGroup=HQ`; design page uses `HQDesign` + `designid`, cart uses `HQCart`.
 */
export function buildDesignGroupDesignToolUrl({
  baseUrl,
  proofItemId,
  fromDesignGroup = DESIGN_TOOL_FROM_DESIGN_GROUP_HQ,
  designId,
}: IBuildDesignGroupDesignToolUrlParams): string {
  const url = new URL(baseUrl);
  url.searchParams.set('fromDesignGroup', fromDesignGroup);
  url.searchParams.set('proofItemId', String(proofItemId));
  if (designId != null) {
    url.searchParams.set('designId', String(designId));
  }
  return url.href;
}

/** Skip query-only fragments and non-absolute URLs — finalize would resolve against HQ origin. */
const finalizeIfAbsoluteOutboundUrl = (url: string): string => {
  if (!url || url.startsWith('?') || !/^https?:\/\//i.test(url)) {
    return url;
  }
  return finalizeHqOutboundUrl(url);
};

/**
 * Builds design tool URL with `proofItemId`, and for corporate users also `clientType=corporate`
 * (applied first so the query reads `...?clientType=corporate&proofItemId=...` where supported).
 */
export function buildDesignToolUrlWithProofItemId(
  baseUrl: string,
  proofItemId: number,
  isCorporate: boolean
): string {
  const trimmed = String(baseUrl ?? '').trim();
  const id = String(proofItemId);

  if (!trimmed) {
    return isCorporate ? `?clientType=corporate&proofItemId=${id}` : `?proofItemId=${id}`;
  }

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (isCorporate) {
      url.searchParams.set('clientType', 'corporate');
    }
    url.searchParams.set('proofItemId', id);
    return finalizeIfAbsoluteOutboundUrl(url.href);
  } catch {
    if (isCorporate) {
      if (trimmed.includes('?')) {
        return finalizeIfAbsoluteOutboundUrl(`${trimmed}&clientType=corporate&proofItemId=${id}`);
      }
      const needsSlashBeforeQuery = /^(https?:\/\/[^/]+)$/i.test(trimmed);
      const base = needsSlashBeforeQuery ? `${trimmed}/` : trimmed;
      return finalizeIfAbsoluteOutboundUrl(`${base}?clientType=corporate&proofItemId=${id}`);
    }
    if (trimmed.includes('?')) {
      return finalizeIfAbsoluteOutboundUrl(`${trimmed}&proofItemId=${id}`);
    }
    const needsSlashBeforeQuery = /^(https?:\/\/[^/]+)$/i.test(trimmed);
    const base = needsSlashBeforeQuery ? `${trimmed}/` : trimmed;
    return finalizeIfAbsoluteOutboundUrl(`${base}?proofItemId=${id}`);
  }
}

/**
 * Appends `clientType=corporate` to the design tool base URL for corporate HQ users.
 * Uses the URL API so paths and existing query strings are handled; if the value is
 * only `https://host` (no path), the result is `https://host/?clientType=corporate`.
 */
export function withCorporateDesignToolClientType(baseUrl: string): string {
  const trimmed = String(baseUrl ?? '').trim();
  if (!trimmed) {
    return trimmed;
  }

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    url.searchParams.set('clientType', 'corporate');
    return url.href;
  } catch {
    if (trimmed.includes('?')) {
      return `${trimmed}&clientType=corporate`;
    }

    const needsSlashBeforeQuery = /^(https?:\/\/[^/]+)$/i.test(trimmed);
    const base = needsSlashBeforeQuery ? `${trimmed}/` : trimmed;
    return `${base}?clientType=corporate`;
  }
}

/**
 * When `isCorporate`, merges `clientType=corporate` into the URL (any path/query).
 * Otherwise returns `url` unchanged. Use for any HQ link into the design tool.
 * Appends `source=HQ` for cross-app Mixpanel attribution.
 */
export function applyCorporateDesignToolParamIfNeeded(url: string, isCorporate: boolean): string {
  const built = isCorporate ? withCorporateDesignToolClientType(url) : url;
  return finalizeIfAbsoluteOutboundUrl(built);
}
